import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../../components/Logo';
import InputField from '../../components/common/InputField';
import PrimaryButton from '../../components/common/PrimaryButton';
import TestimonialCard from '../../components/onboarding/TestimonialCard';
import storage from '../../utils/storage';
import {
  Container,
  FormSection,
  TestimonialSection,
  Title,
  Form,
  FormFooter,
  Link,
  LogoWrapper,
  BrandText,
  SectionTitle
} from '../../styles/components';

const OnboardingStep1 = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Initialize fresh session when landing on step 1
  useEffect(() => {
    // Restart onboarding (this will preserve any existing token)
    storage.restartOnboarding();
    
    // Check for existing step 1 data
    const existingData = storage.getItem('step1Data');
    if (existingData) {
      setFormData(JSON.parse(existingData));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);

    // Get existing stored data to preserve any additional fields (like customToken)
    const existingData = JSON.parse(storage.getItem('step1Data') || '{}');
    storage.setItem('step1Data', JSON.stringify({
      ...existingData,
      ...newFormData
    }));
    
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Debug: Log environment and URL construction
      console.log('Environment check:', {
        NODE_ENV: process.env.NODE_ENV,
        REACT_APP_API_URL: process.env.REACT_APP_API_URL,
        window_location: window.location.href
      });

      const apiUrl = `${process.env.REACT_APP_API_URL}/api/onboarding/step-one`;
      console.log('Request details:', {
        apiUrl,
        formData,
        currentPage: window.location.href
      });

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      }).catch(error => {
        console.error('Fetch error details:', {
          error,
          message: error.message,
          type: error.type,
          stack: error.stack
        });
        throw new Error('Unable to connect to the server. Please ensure the server is running.');
      });

      // Debug: Log response details
      console.log('Response details:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        ok: response.ok
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: `Server returned ${response.status}: ${response.statusText}`
        }));
        throw new Error(errorData.error || 'Failed to submit form');
      }

      const { data } = await response.json();
      console.log('Server response:', data); // Debug log
      
      if (!data.customToken) {
        console.error('No customToken received from server');
      }
      
      // Store Pipedrive IDs using the utility function
      storage.setPipedriveData(data.personId, data.dealId);

      // Store the form data along with the custom token
      const dataToStore = {
        ...formData,
        customToken: data.customToken
      };
      console.log('Storing data:', dataToStore); // Debug log
      storage.setItem('step1Data', JSON.stringify(dataToStore));

      // Verify storage
      const storedData = JSON.parse(storage.getItem('step1Data') || '{}');
      console.log('Verified stored data:', storedData); // Debug log

      // Navigate to next step
      navigate('/onboarding/step-two');
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error.message || 'There was an error processing your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <FormSection>
        <LogoWrapper>
          <Logo />
        </LogoWrapper>
        <Title>Get started with <BrandText>Planet Protection</BrandText></Title>
        <Form onSubmit={handleSubmit}>
          {error && (
            <div style={{ color: 'red', marginBottom: '1rem' }}>
              {error}
            </div>
          )}
          <InputField
            label="Work Email"
            type="email"
            name="email"
            placeholder="name@company.com"
            required
            value={formData.email}
            onChange={handleInputChange}
          />
          <InputField
            label="First Name"
            name="firstName"
            placeholder="First Name"
            required
            value={formData.firstName}
            onChange={handleInputChange}
          />
          <InputField
            label="Last Name"
            name="lastName"
            placeholder="Last Name"
            required
            value={formData.lastName}
            onChange={handleInputChange}
          />
          <PrimaryButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Continue'}
          </PrimaryButton>
          <FormFooter>
            Already have an account? <Link href="/login">Sign in</Link>
          </FormFooter>
        </Form>
      </FormSection>
      <TestimonialSection>
        {/* <TestimonialCard
          quote="Finally, an insurance partner that makes sense for our values. Their platform is refreshingly simple to use, and we can finally see what our insurance impacts. As a B-Corporation, we love that our insurance now actively supports environmental initiatives while providing excellent coverage. It's insurance that actually aligns with our values."
          authorName="Louiza Ziane"
          authorRole="Co-founder, Toast Brewing"
          authorInitials="LZ"
        /> */}
      </TestimonialSection>
    </Container>
  );
};

export default OnboardingStep1; 